import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRoster } from '../lib/roster'
import { useAuth } from '../lib/session'
import {
  gmailComposeUrl,
  gmailLinkTooLong,
  recheckAtHandoff,
  unknownMergeFields,
  unknownMergeMessage,
  LANE_FROM,
  REPLY_TO,
} from '../lib/campaigns'
import {
  agentsById,
  deleteBatch,
  forgetHandedOff,
  groupByBatch,
  loadHeldBulkSends,
  loadQueue,
  markDraft,
  nextHandedOff,
  readHandedOff,
  saveDraftBody,
  skipHeldBulkSend,
  writeHandedOff,
  type HeldBulkSend,
  type QueuedDraft,
} from '../lib/outbox'
import { formatDateTime } from '../lib/format'

/**
 * A held bulk send waiting for her, in exactly the shape Bulk already keeps —
 * see `SavedBlast` in `sender.ts`. Not re-fetched or re-derived differently;
 * this reads the same two tables `Bulk.tsx`'s own `load()` does, so the two
 * screens cannot disagree about which ones are still waiting.
 */
/**
 * Chris, 2026-08-22: "Everything needs to flow through one place... She is
 * not going to check in two places." Moved here from the Outreach tab
 * (`Drafts.tsx`), where a scheduled send lived from 2026-08-21 until that
 * day — Outreach is read-only by design (see that file's own banner), which
 * meant a scheduled send's one actionable control sat on the one screen with
 * no other actionable controls on it at all, while the actual work she does
 * daily (Skip a milestone, Send a reviewed one) lives here. Milestones and
 * drips were already both on this screen (same `outreach_drafts` table
 * `loadQueue` reads); this was the one automated category that was not.
 *
 * **Both a recurring campaign's occurrence AND a one-off blast she composed
 * herself land here (2026-08-23)** — everything `id.startsWith('manual-')`
 * in `campaigns` that has not gone out from Bulk yet. It started 2026-08-22
 * covering only the first of those, via a `generated_from_id` check, because
 * that was the gap in front of Chris that day. A self-composed bulk send —
 * `queueBlast()`'s own insert, no `generated_from_id` set — never had that
 * column, so it never surfaced here despite the Email composer's own confirm
 * screen promising "it is being held... until you open it there" and its
 * Done screen's own button reading "Go to the Outbox". See
 * `loadHeldBulkSends` in `outbox.ts` for the corrected test, and
 * `outbox-shell-for-manual-bulk-flagged-2026-08-23.md` for how this was
 * found. Either way it lands in Bulk exactly like any other campaign row.
 * This section surfaces it here too, WITHOUT pulling Bulk's whole send
 * machinery (test / type-the-count / send, and all the state that goes with
 * it) into a second implementation — that stays in exactly one place,
 * `Bulk.tsx`, and "Review & send" below drops her straight into that same
 * card, already open. Chris, 2026-08-22, on why this is right rather than a
 * shortcut: "A scheduled send does not need individual review of each email —
 * by its nature it is a bulk send campaign, it only needs to present the
 * same as a manually created bulk send" — the same sentence that is why
 * a manual one belongs on exactly this same list, not a separate one.
 *
 * `onCount` reports how many are waiting, so the page-level "Nothing
 * waiting" fallback (below) knows not to show itself while one of these is
 * sitting right above it with nothing else on the queue, and so the nav
 * badge (`navcounts.ts`'s `countOutboxWaiting`, 2026-08-23) can add it to
 * the milestone/drip count without a third copy of this same fetch+filter.
 */
function HeldBulkSends({ onCount }: { onCount: (n: number) => void }) {
  const nav = useNavigate()
  const [waiting, setWaiting] = useState<HeldBulkSend[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirmSkip, setConfirmSkip] = useState<string | null>(null)
  const [skipBusy, setSkipBusy] = useState<string | null>(null)
  const [skipError, setSkipError] = useState<string | null>(null)

  const load = () => {
    loadHeldBulkSends().then(({ rows, error }) => {
      if (error || !rows) {
        setError(error)
        onCount(0)
        return
      }
      setWaiting(rows)
      onCount(rows.length)
    })
  }

  useEffect(() => {
    let cancelled = false
    loadHeldBulkSends().then(({ rows, error }) => {
      if (cancelled) return
      if (error || !rows) {
        setError(error)
        onCount(0)
        return
      }
      setWaiting(rows)
      onCount(rows.length)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onCount is stable (useState setter), not a dep
  }, [])

  /**
   * "Get this off my list" — either deletes it (nothing sent yet) or marks
   * it permanently stopped (a send was started), whichever `skipHeldBulkSend`
   * finds true. See that function's own header for why it's one button doing
   * two different honest things, the same split `Bulk.tsx`'s Delete/Stop
   * already draw. Built 2026-09-08 — see
   * `claude/team-connect-9-8-send-stopped-2026-09-08.md`.
   */
  const skip = async (id: string) => {
    setSkipBusy(id)
    const err = await skipHeldBulkSend(id)
    setSkipBusy(null)
    setConfirmSkip(null)
    if (err) {
      setSkipError(err)
      return
    }
    setSkipError(null)
    load()
  }

  // Errors here are quiet on purpose — this section is an addition to the
  // page, not the page itself, and Bulk (reachable from its own tab) is still
  // the actual record if this fails to load. Loud enough to notice, not loud
  // enough to read as "the whole screen is broken".
  if (error) return <p className="hint">Could not check for bulk sends waiting: {error}</p>
  if (!waiting || waiting.length === 0) return null

  return (
    <div style={{ marginBottom: 18 }}>
      <h3 style={{ marginBottom: 8 }}>
        Bulk sends
        <span className="pill s-pending" style={{ marginLeft: 8 }}>
          {waiting.length} waiting
        </span>
      </h3>
      <p className="hint" style={{ marginTop: -4, marginBottom: 8 }}>
        A bulk campaign, ready to go out to everyone it matched — reviewed and sent as one, not
        one at a time.
      </p>
      {skipError && <p className="error">{skipError}</p>}
      {waiting.map((b) => (
        <section className="sec" key={b.id} style={{ marginBottom: 10 }}>
          <h3>{b.name}</h3>
          <div className="dsubject">{b.subject}</div>
          <div className="hint">{b.audience?.agent_ids?.length ?? 0} agents, when you approve it</div>
          <div className="savebar" style={{ marginTop: 8 }}>
            <button className="btn" onClick={() => nav(`/bulk?open=${encodeURIComponent(b.id)}`)}>
              Review &amp; send
            </button>
            {confirmSkip === b.id ? (
              <>
                <span className="status">
                  Skip this? If nothing has gone out yet it's deleted outright; if a send was
                  started, it's stopped for good and archived in Bulk instead.
                </span>
                <button
                  className="btn ghost"
                  disabled={skipBusy === b.id}
                  onClick={() => void skip(b.id)}
                >
                  {skipBusy === b.id ? 'Skipping…' : 'Yes, skip it'}
                </button>
                <button className="btn ghost" onClick={() => setConfirmSkip(null)}>
                  Keep it
                </button>
              </>
            ) : (
              <button className="btn ghost" onClick={() => setConfirmSkip(b.id)}>
                Skip
              </button>
            )}
          </div>
        </section>
      ))}
    </div>
  )
}

/**
 * The Outbox — everything automated waiting for Renee, one place, checked
 * daily. Milestones, drips, and (2026-08-22) bulk sends — scheduled/recurring
 * and, since 2026-08-23, self-composed too — every automated category, all
 * here, so there is exactly one screen to check rather than a habit of
 * remembering a second one.
 *
 * **Send does not send.** It opens the message in her own Gmail, already filled
 * in; she reads it there, changes anything she likes, and presses send in
 * Gmail. Then she tells this screen it went. Two steps, deliberately: the app
 * cannot see inside that Gmail window, so recording "sent" without asking would
 * be recording something it does not know. The log is worth having only if it
 * is true.
 *
 * Cards are grouped by the batch they came from, because a 52-person job half
 * finished is otherwise 32 loose cards among the birthdays with nothing saying
 * how far she got. A held bulk send does not join that grouping — see
 * `HeldBulkSends` above — because it is reviewed and sent as one bulk action,
 * not card by card; it renders as its own section above the batches.
 */
export default function Outbox() {
  const {
    agents,
    error: rosterError,
    reloadAgents,
    tagsByAgent,
    tagsError,
    tagsLoading,
    reloadTags,
  } = useRoster()
  const { appUser } = useAuth()
  const [rows, setRows] = useState<QueuedDraft[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  /** How many held bulk sends `HeldBulkSends` found waiting — null until it
   *  has actually answered, so the "Nothing waiting" fallback below never
   *  flashes true for a moment before that answer arrives. */
  const [heldBulkCount, setHeldBulkCount] = useState<number | null>(null)
  const [busy, setBusy] = useState<number | null>(null)
  const [handedOff, setHandedOff] = useState<Set<number>>(() => readHandedOff())
  /** Which job is showing on the right. Never null while there is any work. */
  const [selected, setSelected] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)
  /**
   * A warning that outlives the next click.
   *
   * `error` is cleared at the top of `hand()` and `finish()`, which is right
   * for "that didn't work, try again" and wrong for "this screen may no longer
   * match the database, reload". The second kind has to still be there while
   * she carries on down the list, because carrying on down the list is the
   * dangerous part. Only a reload clears it.
   */
  const [standing, setStanding] = useState<string | null>(null)
  /**
   * Edits she has typed into a card but not yet handed over.
   *
   * Kept by draft id rather than on the row, so the stored body stays the
   * queued one until the moment Gmail is actually opened with the new text.
   * Until then nothing has left, and nothing should have been rewritten.
   */
  const [edits, setEdits] = useState<Record<number, string>>({})
  const [copied, setCopied] = useState<number | null>(null)
  /**
   * A failed save, per card, rather than one banner at the top.
   *
   * It arrives asynchronously — typically while she is already two cards
   * further down — and the page-level `error` is cleared by the next Send,
   * Skip or "It went". A warning that the record is now out of step with what
   * was actually sent must not be wiped by the next click, and must say which
   * card it is about.
   */
  const [saveError, setSaveError] = useState<Record<number, string>>({})
  /**
   * The last body actually handed to Gmail for each card.
   *
   * Two saves can be in flight for one card — Send, then more typing, then
   * "Open in Gmail again" — and they can resolve out of order. Without this,
   * the older one's callback wins: it rewrites the row from stale text and
   * deletes the newer edit, so the card silently reverts to a version she has
   * already replaced. A resolving save that is no longer the latest hand-off
   * for that card is ignored.
   */
  const lastHanded = useRef<Record<number, string>>({})

  const load = useCallback(async () => {
    const r = await loadQueue()
    setError(r.error)
    setRows(r.rows)
    /**
     * THE PRUNE ONLY RUNS ON A GOOD READ.
     *
     * A failed `loadQueue` gives back no rows, and pruning against that empties
     * the record of everything she has already handed to Gmail. She works a
     * 52-card batch over two days; her session expires overnight; the page
     * reloads, the read fails, and every card she has already sent is offering
     * "Send" again with nothing anywhere saying which thirty went. The obvious
     * response to that screen is to work through them a second time.
     *
     * When the read fails, the record is left exactly as it was.
     */
    const live = r.rows ? new Set(r.rows.map((x) => x.id)) : null
    setHandedOff((prev) => {
      // The rule itself lives in `outbox.ts` as `nextHandedOff`, where a test
      // can reach it. This is only the plumbing.
      const next = nextHandedOff({ memory: prev, stored: readHandedOff(), live })
      writeHandedOff(next, live ?? undefined)
      return next
    })
  }, [])
  useEffect(() => { void load() }, [load])

  const byId = useMemo(() => agentsById(agents), [agents])
  const batches = useMemo(() => (rows ? groupByBatch(rows) : []), [rows])
  /**
   * Which job is on the right — worked out during the render, not after it.
   *
   * Deciding this in an effect meant one painted frame with nothing selected
   * and an empty right-hand pane, on first load and again every time a job was
   * finished. The effect below still runs, but only to write the answer back
   * into state; the screen never waits for it.
   *
   * When the selected job disappears — its last card sent, skipped, or the
   * batch deleted — the fallback is **the job that took its place in the list**,
   * clamped to the end. Falling back to `batches[0]` would jump her to the
   * oldest job, which is neither where she was nor where she was going.
   */
  const lastIndex = useRef(0)
  const current = useMemo(() => {
    if (batches.length === 0) return null
    const at = batches.findIndex((b) => b.key === selected)
    if (at >= 0) {
      lastIndex.current = at
      return batches[at]
    }
    return batches[Math.min(lastIndex.current, batches.length - 1)]
  }, [batches, selected])

  useEffect(() => {
    const key = current?.key ?? null
    if (key !== selected) setSelected(key)
  }, [current, selected])

  /** Clear a stale confirmation if she moves to another job mid-question. */
  useEffect(() => {
    if (confirmDelete && confirmDelete !== selected) setConfirmDelete(null)
  }, [selected, confirmDelete])

  /**
   * The card as it stands right now, not as it was written.
   *
   * A batch is worked through over days; people leave and people get tagged in
   * between. Checked here, at the moment of the click, rather than once at
   * queue time — see `recheckAtHandoff`.
   */
  const checkFor = (d: QueuedDraft) =>
    recheckAtHandoff({
      agent: byId.get(d.agent_id),
      tags: tagsByAgent.get(d.agent_id) ?? [],
      lane: 'reviewed',
      addressOnCard: (d.facts?.to as string) ?? null,
      // The draft knows what it is for. Without this a farewell is refused at
      // the click for having left, which is the reason it exists.
      trigger: d.trigger,
    })

  if (!rows) {
    return (
      <div className="board tpl">
        <section className="sec">
          {error ? (
            <>
              <p className="error">{error}</p>
              <div className="savebar">
                <button className="btn" onClick={() => void load()}>Try again</button>
              </div>
            </>
          ) : (
            <p className="cnt">Loading…</p>
          )}
        </section>
      </div>
    )
  }

  /**
   * The roster has not arrived yet — so nothing may be judged.
   *
   * `checkFor` asks whether the agent is still in the roster, and while the
   * roster is null the honest answer is "no idea", not "no". Rendering the
   * cards anyway told her every single one of them that the agent no longer
   * existed and to skip it — and Skip was still enabled. Fifty-two valid emails
   * are one obedient minute away from being marked skipped for good.
   */
  if (!agents) {
    // The ROSTER's error, not this screen's. Showing the queue's error here —
    // or nothing — left a permanent "Loading the roster…" with no reason and no
    // way out when the agent fetch failed.
    return (
      <div className="board tpl">
        <section className="sec">
          {rosterError ? (
            <>
              <p className="error">The agent list did not load, so nothing can be sent. {rosterError}</p>
              <div className="savebar">
                <button className="btn" onClick={() => reloadAgents()}>Try again</button>
              </div>
            </>
          ) : (
            <p className="cnt">Loading the agent list…</p>
          )}
        </section>
      </div>
    )
  }

  /** What this card says right now — her edit if she has made one, else what was queued. */
  const bodyOf = (d: QueuedDraft) => edits[d.id] ?? d.body

  const cannotCopy =
    'Your browser would not let the page copy. Select the text and copy it by hand.'

  const copyBody = (d: QueuedDraft) => {
    /*
      `navigator.clipboard` is undefined outside a secure context — an http://
      preview on the LAN, which is exactly how a build gets shown to somebody.
      Reaching straight for `.writeText` there throws synchronously, before any
      promise exists, so the rejection handler below never runs and the button
      does nothing and says nothing: the precise failure it is meant to avoid.
    */
    const clip = navigator.clipboard
    if (!clip?.writeText) return setError(cannotCopy)
    /**
     * COPY IS A SEND BYPASS, SO IT HONOURS THE GATES SEND HONOURS.
     *
     * When a message is too long for a Gmail compose link, the error further
     * down tells her to "copy it across by hand instead" — so the one path that
     * skips `hand` is the one the app itself recommends when sending will not
     * work. An earlier version of this added only the fill-in check and wrote a
     * comment claiming the bypass was closed, which was the worst of both: the
     * small hole shut, the large one open, and a note saying otherwise.
     *
     * The large one is `recheckAtHandoff`. A draft queued last week for
     * somebody who has since been tagged do-not-email, or left the roster,
     * renders a red block and a disabled Send — with Copy sitting beside it,
     * enabled, handing over the finished message.
     */
    if (tagsError) {
      return setError(
        'The agent tags did not load, so this cannot tell who has since asked not to be emailed. ' +
          'Refresh before copying anything out. ' + tagsError,
      )
    }
    const check = checkFor(d)
    if (check.block) return setError(check.block)
    /**
     * A stray fill-in WARNS rather than refusing, and that is deliberate.
     *
     * Send blocks it, because Send is the last step before Gmail. Copy is also
     * how she gets the words somewhere else to work on them — refusing to put
     * them on the clipboard would leave her unable to take the broken text
     * anywhere to fix it. She is told, and the words still copy.
     */
    const stray = unknownMergeFields(bodyOf(d))
    void clip.writeText(bodyOf(d)).then(
      () => {
        setCopied(d.id)
        window.setTimeout(() => setCopied((c) => (c === d.id ? null : c)), 1500)
        // Said after the copy, not instead of it.
        if (stray.length) {
          setError(
            `Copied — but ${unknownMergeMessage(stray).charAt(0).toLowerCase()}${unknownMergeMessage(stray).slice(1)} Fix it before this goes anywhere.`,
          )
        }
      },
      // Refused by the browser rather than by us. Saying so beats a button that
      // looks like it worked and put nothing on the clipboard.
      () => setError(cannotCopy),
    )
  }

  const hand = (d: QueuedDraft) => {
    setError(null)

    // Without tags there is no way to know who has since asked not to be
    // written to, and this is the last gate before an email leaves.
    if (tagsError) {
      setError(
        'The agent tags did not load, so this cannot tell who has since asked not to be emailed. ' +
          'Refresh before sending anything. ' + tagsError,
      )
      return
    }

    const check = checkFor(d)
    if (check.block || !check.to) {
      setError(check.block ?? 'There is no address to send to.')
      return
    }

    /**
     * Her version, not the queued one.
     *
     * `bodyOf` falls back to the stored body, so a card she has not touched
     * behaves exactly as before. The length check below then measures what is
     * actually being handed over — an edit that pushes the message past Gmail's
     * URL limit has to be caught here, not silently truncated by Gmail.
     */
    const body = bodyOf(d)

    /**
     * An emptied box is refused, and this is the guard that matters most on
     * this screen.
     *
     * `bodyOf` treats `''` as a real edit, because it is one. Without this,
     * select-all-and-delete followed by Send would hand Gmail a message with a
     * subject, a recipient and no words — and then the save below would write
     * that emptiness over the queued draft. `edits` is the only other copy and
     * it is cleared on a successful save, so the drafted text would be gone
     * with no undo anywhere. The composer refuses an empty body at
     * `campaigns.ts` `problemsWith`; this is the same refusal, on the field
     * that is now editable after it.
     */
    if (!body.trim()) {
      setError(
        'There is nothing in the message. Type something, or press Skip if you do not want to send it.',
      )
      return
    }

    /**
     * AND THE FILL-IN REFUSAL, on the body as it stands NOW.
     *
     * The draft was checked by `problemsWith` when it was queued, and then this
     * screen made the body editable again. Everything the agent's record could
     * fill in was already filled in at that point, so nothing renders this text
     * a second time — a `{{firstname}}` typed here goes to Gmail as those
     * literal characters, on the last screen before the email leaves.
     *
     * Only UNKNOWN fill-ins stop it. A known one left standing means the agent
     * genuinely has nothing in that column, which `problemsWith` reports as a
     * warning and she is reading the message anyway; refusing it here would
     * block a send the composer deliberately allows.
     */
    const unknown = unknownMergeFields(body)
    if (unknown.length) {
      setError(
        `${unknownMergeMessage(unknown)} Take it out, or type what it should say in yourself.`,
      )
      return
    }

    const url = gmailComposeUrl({ to: check.to, subject: d.subject, body })
    if (gmailLinkTooLong(url)) {
      setError(
        'That message is too long to hand to Gmail in one go — Gmail would cut it off. ' +
          'Copy it across by hand instead, or shorten it.',
      )
      return
    }

    /**
     * Only counted as handed over if the window actually opened.
     *
     * A pop-up blocker returns null here. Without this check, a blocked window
     * still armed the "It went" button, so the next click logged a send for an
     * email that never appeared on screen, let alone left.
     *
     * **`noopener` must not be in the features string.** The spec says
     * `window.open` returns null whenever `noopener` is set — successfully or
     * not — so the check above would have fired on every single open, "It went"
     * would never have appeared on any card, and the only lane that works would
     * have been unusable. `opener` is cleared afterwards instead, which gets the
     * same protection without throwing away the answer.
     */
    /**
     * A window named after this card, rather than `_blank`.
     *
     * `_blank` opens a fresh tab every time, so "Open in Gmail again" leaves a
     * *second* Gmail draft of the same message alongside the first — two drafts
     * sitting in her Gmail is a small invitation to send both. A stable name
     * reuses the tab she already has open for this card.
     */
    const w = window.open(url, `gmail-draft-${d.id}`)
    if (!w) {
      setError(
        'Your browser blocked the Gmail window. Allow pop-ups for this site, then press Send again.',
      )
      return
    }
    try {
      w.opener = null
    } catch {
      // Cross-origin by the time this runs on some browsers. Not worth failing over.
    }
    setHandedOff((prev) => {
      const next = new Set(prev).add(d.id)
      writeHandedOff(next)
      return next
    })

    /**
     * Write the edit down, now that it has actually gone somewhere.
     *
     * After `window.open`, deliberately. Opening the window has to be the first
     * thing that happens on this click or the pop-up blocker eats it — an
     * `await` before it breaks the user-gesture chain — so the save is fired
     * afterwards and not waited on.
     *
     * If the save fails, the email has still been handed over and she should
     * still press "It went". So this reports rather than blocks, and says which
     * of the two things went wrong: the sending did not, the remembering did.
     */
    lastHanded.current[d.id] = body
    if (body !== d.body) {
      void saveDraftBody(d.id, body).then((err) => {
        // Superseded while this was in flight — she carried on typing and handed
        // over again. Whatever this call did, it is no longer the truth about
        // this card, and acting on it would undo the newer edit.
        if (lastHanded.current[d.id] !== body) return

        if (err) {
          setSaveError((prev) => ({
            ...prev,
            [d.id]: `Gmail opened with your edit, but it could not be saved here — the record will ` +
              `keep the old wording unless you press Send again. ${err}`,
          }))
          return
        }
        setSaveError((prev) => {
          const next = { ...prev }
          delete next[d.id]
          return next
        })
        setRows((prev) => (prev ?? []).map((r) => (r.id === d.id ? { ...r, body } : r)))
        setEdits((prev) => {
          // Only drop the pending edit if it is still the one that was saved.
          if (prev[d.id] !== body) return prev
          const next = { ...prev }
          delete next[d.id]
          return next
        })
      })
    }
  }

  const removeBatch = async (b: { key: string; rows: QueuedDraft[] }) => {
    setDeleting(true)
    setError(null)
    const ids = b.rows.map((r) => r.id)
    const { deletedIds, error: err } = await deleteBatch(ids)
    setDeleting(false)
    setConfirmDelete(null)

    /**
     * Remove exactly the rows the database said it removed.
     *
     * Not the first N of them. A chunked delete can come back short without an
     * error — a card finished in another tab, a row already gone — and taking
     * a prefix would clear cards that still exist while leaving cards for rows
     * that do not. A card with no row behind it still has a Send button, and
     * pressing it emails a real agent with nothing left to record it.
     */
    if (deletedIds.length > 0) {
      const gone = new Set(deletedIds)
      setRows((prev) => (prev ?? []).filter((r) => !gone.has(r.id)))
      gone.forEach((id) => forgetHandedOff(id))
    }
    /**
     * Kept apart from `error`, which `hand()` and `finish()` both clear on
     * their first line. "Some of these may not have been deleted — reload" is
     * precisely the sentence that must survive the next click, because the next
     * click is her carrying on down a list that may be lying to her.
     */
    if (err) setStanding(err)
  }

  const finish = async (d: QueuedDraft, status: 'sent' | 'skipped') => {
    setBusy(d.id)
    setError(null)
    /**
     * The address that was actually used, not the one on the card.
     *
     * When an agent's email changed since queueing, the card says so and Gmail
     * opens to the new one — but the log was written from `d.facts`, which
     * still held the old address. The only record of who was emailed would have
     * named somebody's former brokerage account.
     */
    const check = checkFor(d)
    const facts = { ...(d.facts ?? {}), ...(status === 'sent' && check.to ? { to: check.to } : {}) }
    const err = await markDraft(d, status, facts, appUser?.id ?? null)
    setBusy(null)
    if (err) return setError(err)
    setRows((prev) => (prev ?? []).filter((r) => r.id !== d.id))
    /**
     * Cleared from the store unconditionally, not only when this tab happens to
     * have it in memory. Another tab may have been the one that handed it off,
     * and leaving its id behind lets the merge in `hand()` promote a card that
     * has already been finished.
     */
    forgetHandedOff(d.id)
    setHandedOff((prev) => {
      if (!prev.has(d.id)) return prev
      const next = new Set(prev)
      next.delete(d.id)
      return next
    })
  }

  return (
    <div className="board tpl outbox">
      {/* Survives the next click, unlike `error`. See the state declaration. */}
      {standing && <p className="error">{standing} </p>}
      {error && <p className="error">{error}</p>}

      {tagsError && (
        <>
          <p className="error">
            The agent tags did not load, so this cannot tell who has since asked not to be emailed.
            Sending is switched off until they do. {tagsError}
          </p>
          {/*
            A way out, in the app. Without this a single dropped request on a
            Monday morning leaves her with no emailing at all and an instruction
            to "refresh" that she has no reason to trust has worked.
          */}
          <div className="savebar">
            <button className="btn" disabled={tagsLoading} onClick={() => reloadTags()}>
              {tagsLoading ? 'Trying…' : 'Try loading the tags again'}
            </button>
          </div>
        </>
      )}

      <div className="notice">
        Emails waiting to go out. <strong>{rows.length} waiting.</strong> Pressing Send opens the
        message in your Gmail — it does not send it. Read it there, send it, then come back and
        say it went.
      </div>

      {/*
        Said once at the top rather than on all 52 cards.

        These emails leave from her Real Brokerage mailbox, and the browser
        decides which mailbox that is — not this app. Signed into that account,
        Google passes straight through and she never sees this sentence matter.
        Signed into the other one, Google stops and asks, which is the whole
        point of the change that put it there.

        Naming the address is what makes this actionable: "use the right
        profile" means nothing to someone who does not know which one they are
        in, and the answer is visible in the corner of the window she is
        already in.
      */}
      {rows.length > 0 && (
        <div className="hint" style={{ marginTop: -6, marginBottom: 10 }}>
          These go out from {LANE_FROM.reviewed}, so use the browser you are signed into that
          account with. If Google asks you to pick an account, that is it telling you this window
          is signed into the other one — pick the Real Brokerage address and carry on.
        </div>
      )}

      <HeldBulkSends onCount={setHeldBulkCount} />

      {/*
        Both counts have to say zero, not just the reviewed queue — a held
        bulk send sitting in the section just above would make "Nothing
        waiting" a lie standing right underneath it. `heldBulkCount === null`
        (still loading) also holds this back, rather than flashing "Nothing
        waiting" for a moment before that answer arrives and then possibly
        taking it back.
      */}
      {rows.length === 0 && heldBulkCount === 0 && <div className="hint">Nothing waiting. Nice.</div>}

      {/*
        Two panes: the jobs on the left, the one she is working on to the right.

        It replaced an accordion of collapsed groups, and the accordion was
        wrong in four ways at once. Only one group could be open, so the whole
        workload was never visible. Everything started closed, so a batch of one
        was a folder to open before a single email could be read. Nothing showed
        a date, so two groups both reading `BIRTHDAY · 2 LEFT` sat on top of one
        another with no way to tell them apart. And the page used the left 45% of
        the window while the roster next door has been two-pane all along.

        Nothing collapses here. Picking a job on the left shows all of its cards
        on the right, which is the shape of the work: choose the job once, then
        grind down it.
      */}
      {rows.length > 0 && (
        <div className="outbox2">
          <aside className="obgroups">
            {batches.map((b) => (
              <button
                key={b.key}
                type="button"
                className={`obgroup${b.key === selected ? ' active' : ''}`}
                onClick={() => setSelected(b.key)}
              >
                <span className="obgroup-label">{b.label}</span>
                <span className="obgroup-meta">
                  {b.rows.length} left
                  {b.queuedAt && <> · queued {formatDateTime(b.queuedAt)}</>}
                </span>
              </button>
            ))}
          </aside>

          <section className="obcards">
            {current && (
              <section className="sec" style={{ marginBottom: 14 }}>
                <h3>
                  {current.label}
                  <span className="pill s-pending">{current.rows.length} left</span>
                </h3>
                <div className="hint">
                  Queued {current.queuedAt ? formatDateTime(current.queuedAt) : 'at an unknown time'}
                </div>

                {/*
                  Delete removes the rows. Skip, on each card, keeps them.

                  They are different statements, not two strengths of the same
                  one: Skip says she read this one and decided against it, which
                  is a decision about a person and worth keeping; Delete says
                  the batch should never have existed, and nobody was contacted,
                  so a `skipped` row would record a decision she never made.

                  Confirmed, because there is no undo. The count is in the
                  question rather than only on the button, so the thing she is
                  agreeing to is the thing she reads.
                */}
                {confirmDelete === current.key ? (
                  <div className="savebar">
                    <span className="cnt" style={{ margin: 0 }}>
                      Delete {current.rows.length}{' '}
                      {current.rows.length === 1 ? 'email' : 'emails'} without sending?
                    </span>
                    <button
                      className="btn"
                      disabled={deleting}
                      onClick={() => void removeBatch(current)}
                    >
                      {deleting ? 'Deleting…' : 'Yes, delete them'}
                    </button>
                    <button
                      className="btn ghost"
                      disabled={deleting}
                      onClick={() => setConfirmDelete(null)}
                    >
                      Keep them
                    </button>
                  </div>
                ) : (
                  <div className="savebar">
                    <button className="btn ghost" onClick={() => setConfirmDelete(current.key)}>
                      Delete this batch
                    </button>
                  </div>
                )}
              </section>
            )}

            {current?.rows.map((d) => {
              const agent = byId.get(d.agent_id)
              const check = checkFor(d)
              const to = check.to || (d.facts?.to as string) || '(no address)'
              return (
                <section className="sec" key={d.id} style={{ marginBottom: 14 }}>
                  <div className="emeta">
                    <span>
                      {agent ? `${agent.first_name ?? ''} ${agent.last_name ?? ''}`.trim() : d.agent_id}
                      {' · '}
                      {to}
                    </span>
                    <span className="hint">from {LANE_FROM.reviewed}</span>
                  </div>
                  {/*
                    Said on the card itself, not only when she presses Send. A
                    reason to skip is worth seeing before she has read three
                    paragraphs written to somebody who has left.
                  */}
                  {check.block && <p className="error">{check.block}</p>}
                  {check.note && <p className="hint">{check.note}</p>}
                  <div className="dsubject">{d.subject}</div>
                  {/*
                    Editable, as it was in the old dashboard.

                    The subject deliberately is not. It is the batch's identity
                    — `groupByBatch` labels a group from it and the Outreach tab
                    lists by it — so letting it drift per card would quietly
                    split one job into several on every screen that counts them.

                    A typo is spotted here, in the list, and not in the Gmail
                    window where fixing it does not come back. Whatever is in
                    this box is what Gmail is opened with, and it is written
                    back to the draft at that moment, so the record and the
                    email say the same thing.
                  */}
                  <textarea
                    className="draftbody"
                    rows={8}
                    value={bodyOf(d)}
                    aria-label={`Message to ${to}`}
                    onChange={(e) =>
                      setEdits((prev) => ({ ...prev, [d.id]: e.target.value }))
                    }
                  />
                  {bodyOf(d) !== d.body && (
                    <p className="hint" style={{ marginTop: 6 }}>
                      Edited. Gmail will open with this version, and it will be saved here when you
                      press Send.
                    </p>
                  )}
                  {/* On the card, not in the page banner — see `saveError`. */}
                  {saveError[d.id] && <p className="error">{saveError[d.id]}</p>}
                  <div className="savebar">
                    <button
                      className="btn"
                      disabled={busy === d.id || !!check.block || !!tagsError}
                      onClick={() => hand(d)}
                    >
                      {handedOff.has(d.id) ? 'Open in Gmail again' : 'Send'}
                    </button>
                    {/*
                      Only offered once she has actually been handed the message.
                      "It went" is her word for something she did in another
                      window; offering it before that window has even opened
                      invites a click that records a send nobody made.
                    */}
                    {handedOff.has(d.id) && (
                      <button className="btn" disabled={busy === d.id} onClick={() => void finish(d, 'sent')}>
                        {busy === d.id ? 'Saving…' : 'It went'}
                      </button>
                    )}
                    {/*
                      Copies the message only, not the subject. It is for
                      getting the words somewhere else — a text, a different
                      mailbox — and the Gmail link already carries both.
                    */}
                    <button className="btn ghost" onClick={() => copyBody(d)}>
                      {copied === d.id ? 'Copied' : 'Copy'}
                    </button>
                    <button className="btn ghost" disabled={busy === d.id} onClick={() => void finish(d, 'skipped')}>
                      Skip
                    </button>
                    <span className="status">
                      {/*
                        Naming the account is the only check that survives
                        Google changing its mind about `authuser`. The link
                        asks for her Real Brokerage mailbox by address rather
                        than by position in her account list, but if Google
                        ignores that, the draft lands in whichever account is
                        her current default — and nothing here can tell. She
                        can, in one glance, in the window she is already in.
                      */}
                      {handedOff.has(d.id)
                        ? `Check the From line says ${LANE_FROM.reviewed}, send it, then press "It went".`
                        : `Replies come back to ${REPLY_TO}. Add your signature in Gmail before sending.`}
                    </span>
                  </div>
                </section>
              )
            })}
          </section>
        </div>
      )}
    </div>
  )
}
